import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDebouncedFn } from 'beautiful-react-hooks';
import { addDays, format } from 'date-fns';
import { v4 as uuidv4 } from 'uuid';

import { fetchReport, generateReport } from '../../../services/reports';
import { Card, Typography, EmptyComponent, Spinner } from '../../../components';
import { BarChartGraph, MapChartGraph, PieChartGraph, TableChartGraph, FiltersSection } from '../../../components/graphs';
import { REPORT_TYPES, ROLES, REPORT_VIEW_OPTIONS, REPORT_VIEW_OPTIONS_FOR_ROLE } from '../../../../constants/general.constants';
import * as S from './InformationTab.styles';

/**
 * Constants
 */

const MAX_POLLING_ATTEMPTS = 60;

/**
 * State
 */

let pollingTimer = null;

export const InformationTab = ({ role }) => {
	const [pageStatus, setPageStatus] = useState('idle');
	const [report, setReport] = useState(null);

	const filterRef = useRef();
	const pollingCount = useRef(0);

	// Create valid view options for role
	const validViewOptions = REPORT_VIEW_OPTIONS_FOR_ROLE(role == null, [role]);

	const onChangeCallback = useDebouncedFn(async () => {
		setPageStatus('loading');
		const accessKey = uuidv4();
		try {
			const {
				data: {
					result: { report },
				},
			} = await generateReport({
				isPublic: role == null,
				type: REPORT_TYPES.INFORMATION,
				graphs: [],
				accessKey,
				...filterRef.current.value,
			});
			if (report.isComplete === false) {
				// Report now generates asynchronously — poll until it is ready
				pollForReportUpdates(accessKey);
			} else {
				setReport(report);
				setPageStatus('success');
			}
		} catch (error) {
			// Check if timeout error
			if (error?.response?.status === 503) {
				pollForReportUpdates(accessKey);
			} else {
				setPageStatus('error');
			}
		}
	}, 200);

	async function initializeData() {
		setPageStatus('loading');
		const accessKey = uuidv4();
		try {
			const {
				data: {
					result: { report },
				},
			} = await generateReport({
				isPublic: role == null,
				type: REPORT_TYPES.INFORMATION,
				graphs: [],
				viewOption: validViewOptions[0],
				businessLocationId: 'all',
				startDate: format(addDays(new Date(), -365), 'MM-dd-yyyy'),
				endDate: format(addDays(new Date(), 1), 'MM-dd-yyyy'),
				countyId: 'all',
				accessKey,
			});
			if (report.isComplete === false) {
				// Report now generates asynchronously — poll until it is ready
				pollForReportUpdates(accessKey);
			} else {
				setReport(report);
				setPageStatus('success');
			}
		} catch (error) {
			// Check if timeout error
			if (error?.response?.status === 503) {
				pollForReportUpdates(accessKey);
			} else {
				setPageStatus('error');
			}
		}
	}

	const stopPolling = () => {
		if (pollingTimer != null) clearInterval(pollingTimer);
		pollingTimer = null;
		pollingCount.current = 0;
	};

	const pollForReportUpdates = (accessKey) => {
		// Cancel any previous polling run before starting a new one
		stopPolling();

		const checkReport = async () => {
			try {
				// Fetch report
				const {
					data: {
						result: { report },
					},
				} = await fetchReport({ accessKey });

				// Check if report is complete
				if (report.isComplete === true) {
					stopPolling();

					// Surface an error if generation failed, otherwise show the report
					if (report.hasError === true) {
						setPageStatus('error');
					} else {
						setReport(report);
						setPageStatus('success');
					}
					return;
				}
			} catch (error) {
				// Transient error (gateway timeout / report not ready yet). The
				// attempt still counts below, so polling can never run forever.
			} finally {
				// Update poll count
				pollingCount.current += 1;

				// Show error if exceeded maximum poll attempts
				if (pollingTimer != null && pollingCount.current > MAX_POLLING_ATTEMPTS) {
					stopPolling();
					setPageStatus('error');
				}
			}
		};

		// Check immediately so fast reports do not wait a full interval, then poll
		checkReport();
		pollingTimer = setInterval(checkReport, 5 * 1000); // 5 seconds
	};

	useEffect(() => {
		// Load initial data
		if (pageStatus === 'idle') {
			initializeData();
		}
	}, [pageStatus]);

	// Stop polling when the component unmounts
	useEffect(
		() => () => {
			if (pollingTimer != null) clearInterval(pollingTimer);
			pollingTimer = null;
		},
		[]
	);

	const renderGraphs = () => {
		if (report?.timepoints.length > 0) {
			if (role == null) {
				return (
					<>
						<BarChartGraph className="col6" data={report?.timepoints[report.timepoints.length - 1]?.data?.categoryType} title="Category Type" />
						<BarChartGraph className="col6" data={report?.timepoints[report.timepoints.length - 1]?.data?.careerClusters} title="Career Cluster" />
						<PieChartGraph className="col6" data={report?.timepoints[report.timepoints.length - 1]?.data?.employabilitySkills} title="Employability Skills" />
						<TableChartGraph className="col6" data={report?.timepoints[report.timepoints.length - 1]?.data?.curriculumStandards} title="Curriculum Standard" labels={['Curriculum Standard', '# of Resources']} />
						<TableChartGraph className="col6" data={report?.timepoints[report.timepoints.length - 1]?.data?.companies} title="Companies" labels={['Company', '# of Resources']} fields={['name', 'informationCount']} />
						<TableChartGraph className="col6" data={report?.timepoints[report.timepoints.length - 1]?.data?.schools} title="Schools" labels={['School', '# of Resources']} fields={['name', 'informationCount']} />
					</>
				);
			} else if (role === ROLES.TEACHER) {
				return (
					<>
						<BarChartGraph className="col6" data={report?.timepoints[report.timepoints.length - 1]?.data?.categoryType} title="Category Type" />
						<BarChartGraph className="col6" data={report?.timepoints[report.timepoints.length - 1]?.data?.careerClusters} title="Career Cluster" />
						<PieChartGraph className="col6" data={report?.timepoints[report.timepoints.length - 1]?.data?.employabilitySkills} title="Employability Skills" />
						<TableChartGraph className="col6" data={report?.timepoints[report.timepoints.length - 1]?.data?.curriculumStandards} title="Curriculum Standard" labels={['Curriculum Standard', '# of Resources']} />
						<TableChartGraph className="col6" data={report?.timepoints[report.timepoints.length - 1]?.data?.companies} title="Companies" labels={['Company', '# of Resources']} fields={['name', 'informationCount']} />
					</>
				);
			} else if (role === ROLES.BUSINESS) {
				return (
					<>
						<BarChartGraph className="col6" data={report?.timepoints[report.timepoints.length - 1]?.data?.gradeLevel} title="Grade Level" />
						<MapChartGraph className="col6" title="County" data={report?.timepoints[report.timepoints.length - 1]?.data?.county} />
						<PieChartGraph className="col6" data={report?.timepoints[report.timepoints.length - 1]?.data?.employabilitySkills} title="Employability Skills" />
						<BarChartGraph className="col6" data={report?.timepoints[report.timepoints.length - 1]?.data?.businessLocations} title="Company Locations" />
						<TableChartGraph className="col6" data={report?.timepoints[report.timepoints.length - 1]?.data?.views.filter((datum) => datum.viewCount > 1)} title="Views" labels={['Resource Name', '# of Views']} fields={['title', 'viewCount']} />
						<TableChartGraph className="col6" data={report?.timepoints[report.timepoints.length - 1]?.data?.saves.filter((datum) => datum.saveCount > 1)} title="Clicks" labels={['Resource Name', '# of Clicks']} fields={['title', 'linkClickCount']} />
						<TableChartGraph className="col6" data={report?.timepoints[report.timepoints.length - 1]?.data?.saves.filter((datum) => datum.saveCount > 1)} title="Saves" labels={['Resource Name', '# of Saves']} fields={['title', 'saveCount']} />
						<TableChartGraph className="col6" data={report?.timepoints[report.timepoints.length - 1]?.data?.views.filter((datum) => datum.viewCount > 1)} title="Downloads" labels={['Resource Name', '# of Downloads']} fields={['title', 'fileDownloadCount']} />
					</>
				);
			} else if (role === ROLES.ADMIN || role === ROLES.SUPER) {
				return (
					<>
						<BarChartGraph className="col6" data={report?.timepoints[report.timepoints.length - 1]?.data?.categoryType} title="Category Type" />
						<BarChartGraph className="col6" data={report?.timepoints[report.timepoints.length - 1]?.data?.careerClusters} title="Career Cluster" />
						<PieChartGraph className="col6" data={report?.timepoints[report.timepoints.length - 1]?.data?.employabilitySkills} title="Employability Skills" />
						<TableChartGraph className="col6" data={report?.timepoints[report.timepoints.length - 1]?.data?.curriculumStandards} title="Curriculum Standard" labels={['Curriculum Standard', '# of Resources']} />
						<TableChartGraph className="col6" data={report?.timepoints[report.timepoints.length - 1]?.data?.companies} title="Companies" labels={['Company', '# of Resources']} fields={['name', 'informationCount']} />
						<TableChartGraph className="col6" data={report?.timepoints[report.timepoints.length - 1]?.data?.schools} title="Schools" labels={['School', '# of Resources']} fields={['name', 'informationCount']} />
					</>
				);
			}
		}
	};

	return (
		<S.Wrapper>
			<FiltersSection ref={filterRef} onChangeCallback={onChangeCallback} />
			{pageStatus === 'loading' ? (
				<Spinner />
			) : report ? (
				<S.GridWrapper>
					<S.CardWrapper className="col12">
						{report?.metrics?.map((metric) => (
							<Card className="metric-card" key={metric.id}>
								<Typography tag="h1" weight="extrablack">
									{metric.data}
								</Typography>
								<Typography tag="h6" weight="semibold">
									{metric.name}
								</Typography>
							</Card>
						))}
					</S.CardWrapper>
					{renderGraphs()}
				</S.GridWrapper>
			) : (
				<EmptyComponent title="Gathering report data" message="We are currently processing report data. Check back later to view full reports." icon={['fal', 'chart-bar']} />
			)}
		</S.Wrapper>
	);
};

InformationTab.propTypes = {
	role: PropTypes.string,
};
